// dagTaskRunner.js — RCA lentidão fixes A/H/I/B: monta o runTask de UMA task do DAG
// com tudo que o worker precisa além dos loop-args do caller:
//   A — excludeTools update_todo (o EXECUTOR flipa o board; update_todo no worker
//       era 2 round-trips LLM desperdiçados por task, medidos em prod);
//   H — teto wall-clock POR TASK (withTaskDeadline, 15min): estourou ⇒ aborta o
//       loop DA task e devolve blocked/task_timeout (recuperável, contrato WSX);
//   I — progresso vivo (makeDagTaskEventSink: loop_step/tool_call → todo_progress);
//   B — runCtx.parallel da wave suprime deltas de streaming (bolha única não
//       embaralha) e namespace-ia tool ids (call_0 colide entre loops concorrentes).
// Extraído do SessionWorkspace (dp-loc ratchet): o spine só injeta as closures.

import { makeDagTaskEventSink, markBlockedTaskOutcome } from '../../../lib/loop/dagPlanBridge.js';
import { withTaskDeadline, DAG_TASK_DEADLINE_MS } from '../../../lib/loop/taskDeadline.js';

/** Mensagem de sistema do estouro do teto por task (fix H). Pura. */
export function taskTimeoutNotice(task, ms = DAG_TASK_DEADLINE_MS) {
  const label = task?.name || task?.goal || task?.id || 'task';
  return {
    role: 'system',
    content: `⏱️ Task "${label}" estourou o teto de ${Math.round(ms / 60000)}min e foi marcada como bloqueada (recuperável — reexecute ou divida a task).`,
  };
}

/**
 * Constrói o runTask(task, runCtx) das tasks do DAG.
 * @param {{
 *   runLoop: (args:object)=>Promise<any>,          // runLocalAgentLoop
 *   loopArgsForTask: (task:object)=>object,        // args do loop SEM signal/onEvent (o runner injeta)
 *   onEvent: (evt:object)=>void,                   // sink principal do turno
 *   dispatchProgress: (p:object)=>void,            // dispatchPlan(sessionId, todo_progress)
 *   parentSignal?: AbortSignal,                    // Stop do usuário
 *   deadline?: boolean,                            // false ⇒ cru (o caller envolve o SEAM inteiro — caso scaffold)
 *   deadlineMs?: number,
 *   onTimeout?: (task:object)=>void,               // aviso visível (pushMsg)
 * }} cfg
 * @returns {(task:object, runCtx?:object)=>Promise<any>}
 */
export function makeDagTaskRunner({ runLoop, loopArgsForTask, onEvent, dispatchProgress, parentSignal, deadline = true, deadlineMs = DAG_TASK_DEADLINE_MS, onTimeout }) {
  const inner = (task, runCtx = {}) => {
    const baseArgs = loopArgsForTask(task);
    // PERF-2026-07 — retry com feedback do oracle: injeta o feedback como uma
    // mensagem de sistema ANTES das messages da task, para o agente saber o que
    // corrigir. Em runtime normal (sem retry), runCtx.oracleFeedback é undefined.
    const messages = runCtx.oracleFeedback
      ? [
          { role: 'system', content: `🔄 RETRY (tentativa ${runCtx.retryAttempt || 1}): A tentativa anterior foi reprovada pela verificação. Corrija os problemas abaixo e refaça a task.\n\n${runCtx.oracleFeedback}` },
          ...(baseArgs.messages || []),
        ]
      : baseArgs.messages;
    return runLoop({
      ...baseArgs,
      messages,
      excludeTools: ['update_todo'], // fix A
      signal: runCtx.signal || parentSignal,
      onEvent: makeDagTaskEventSink(task.id, onEvent, {
        dispatchProgress,
        suppressDeltas: !!runCtx.parallel, // fix B
        namespaceToolIds: !!runCtx.parallel,
      }),
    }).then(markBlockedTaskOutcome);
  };
  return deadline ? withTaskDeadline(inner, { ms: deadlineMs, parentSignal, onTimeout }) : inner;
}

/**
 * fix H (review adversarial) — o teto por task tem que cobrir o SEAM INTEIRO: com o
 * role scaffold ligado (task complexa), o Thinker rodava ANTES do deadline do Worker
 * (até ~30min de pré-pass fora do teto). O caller monta o seam (scaffold ou runner
 * cru) e envolve AQUI — um único deadline por task, do primeiro token do thinker ao
 * último do worker.
 * @param {(task:object, runCtx?:object)=>Promise<any>} seam
 * @param {{ parentSignal?: AbortSignal, onTimeout?: (task:object)=>void, deadlineMs?: number }} [opts]
 * @returns {(task:object, runCtx?:object)=>Promise<any>}
 */
export function withDagDeadline(seam, { parentSignal, onTimeout, deadlineMs = DAG_TASK_DEADLINE_MS } = {}) {
  return withTaskDeadline(seam, { ms: deadlineMs, parentSignal, onTimeout });
}

/**
 * fix B (review adversarial) — sink do THINKER numa wave paralela: sem isto, 2-3
 * thinkers concorrentes streamavam token/reasoning crus no MESMO onEvent (bolha
 * embaralhada) e tool ids sem namespace (card errado). Serial ⇒ passthrough puro.
 */
export function makeThinkerEventSink(taskId, onEvent, runCtx) {
  const parallel = !!runCtx?.parallel;
  if (!parallel) return onEvent;
  return makeDagTaskEventSink(taskId, onEvent, { suppressDeltas: true, namespaceToolIds: true });
}
